import React from 'react';

import Button from '@atlaskit/button/new';
import CopyIcon from '@atlaskit/icon/core/copy';
import Tooltip from '@atlaskit/tooltip/Tooltip';

type CopyProps = {
	source: string;
	testId?: string;
};

const TOOLTIP_MESSAGE = {
	SUCCESS: 'Copied!',
	FAILURE: 'Copy to clipboard failed',
};

/**
 * __Copy__
 *
 * A copy displays a button to copy the example source code.
 *
 */
const Copy = ({ source, testId }: CopyProps): React.JSX.Element | null => {
	const [tooltipMessage, setTooltipMessage] = React.useState<string | null>(null);
	const hideTooltipTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	const setTemporaryTooltipMessage = React.useCallback(
		(message: string) => {
			setTooltipMessage(message);
			if (hideTooltipTimeout.current) {
				clearTimeout(hideTooltipTimeout.current);
			}
			hideTooltipTimeout.current = setTimeout(() => {
				setTooltipMessage(null);
			}, 2000);
		},
		[setTooltipMessage],
	);

	const resetMessage = React.useCallback(() => {
		setTooltipMessage(null);
		if (hideTooltipTimeout.current) {
			clearTimeout(hideTooltipTimeout.current);
		}
	}, [setTooltipMessage]);

	const writeToClipboard = React.useCallback(() => {
		navigator.clipboard
			.writeText(source)
			.then(() => setTemporaryTooltipMessage(TOOLTIP_MESSAGE.SUCCESS))
			.catch(() => setTemporaryTooltipMessage(TOOLTIP_MESSAGE.FAILURE));
	}, [source, setTemporaryTooltipMessage]);

	const isVisible = Boolean(typeof window !== 'undefined' && navigator?.clipboard?.writeText);

	return isVisible ? (
		<Tooltip content={tooltipMessage} position="bottom" testId={testId}>
			{(tooltipProps) => (
				<Button
					{...tooltipProps}
					appearance="subtle"
					iconAfter={CopyIcon}
					onClick={(e) => {
						tooltipProps.onClick(e);
						writeToClipboard();
					}}
					onMouseEnter={resetMessage}
					onFocus={(e) => {
						tooltipProps.onFocus(e);
						resetMessage();
					}}
					aria-live="assertive"
					aria-atomic="true"
					testId={testId ? `${testId}--button` : undefined}
				>
					Copy code
				</Button>
			)}
		</Tooltip>
	) : null;
};

export default Copy;
